import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import styled, { css } from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { selectField } from '../fields/widgets/select-field';
import { videoPosterField, videoSourceField } from '../fields/widgets/video-fields';
import { BODY_LINE_HEIGHT, BODY_TEXT_SIZES, FONT_ROLES, TEXT_COLOR_ROLES } from '../fields/tokens';
import { interpolateFromPuck } from '../interpolation';
import {
  EMPTY_VIDEO_ASSET_SOURCE,
  NO_VIDEO_POSTER,
  normalizeVideoAccessibleName,
  normalizeVideoPoster,
  normalizeVideoSource,
} from './video-source';
import type { NormalizedVideoSource, VideoPoster, VideoSource } from './video-source';

export type VideoWidth = 'custom' | 'fit' | 'full' | 'large' | 'medium' | 'small';
export type VideoAlign = 'center' | 'left' | 'right';
export type VideoMobileWidth = 'full' | 'keep';
export type VideoAspectRatio = 'original' | 'portrait' | 'square' | 'wide';

export type VideoLayout = {
  width: VideoWidth;
  customWidth: number;
  align: VideoAlign;
  mobile: VideoMobileWidth;
  aspectRatio: VideoAspectRatio;
};

export type VideoProps = {
  source: VideoSource;
  poster: VideoPoster;
  accessibleName: string;
  caption: string;
  layout: VideoLayout;
};

const DEFAULT_CUSTOM_WIDTH = 640;
const MAX_CUSTOM_WIDTH = 3840;
const DEFAULT_LAYOUT: VideoLayout = {
  width: 'full',
  customWidth: DEFAULT_CUSTOM_WIDTH,
  align: 'center',
  mobile: 'full',
  aspectRatio: 'original',
};

const WIDTH_OPTIONS: ReadonlyArray<{ label: string; value: VideoWidth }> = [
  { label: 'Full width', value: 'full' },
  { label: 'Fit content', value: 'fit' },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
  { label: 'Custom', value: 'custom' },
];
const ALIGN_OPTIONS: ReadonlyArray<{ label: string; value: VideoAlign }> = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];
const MOBILE_OPTIONS: ReadonlyArray<{ label: string; value: VideoMobileWidth }> = [
  { label: 'Fill available width', value: 'full' },
  { label: 'Keep chosen width', value: 'keep' },
];
const ASPECT_RATIO_OPTIONS: ReadonlyArray<{ label: string; value: VideoAspectRatio }> = [
  { label: 'Original', value: 'original' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Square (1:1)', value: 'square' },
  { label: 'Portrait (9:16)', value: 'portrait' },
];

const VIDEO_WIDTHS: Record<Exclude<VideoWidth, 'custom' | 'fit'>, string> = {
  full: '100%',
  small: 'var(--dcm-cms-video-size-small, 240px)',
  medium: 'var(--dcm-cms-video-size-medium, 480px)',
  large: 'var(--dcm-cms-video-size-large, 800px)',
};
const VIDEO_MARGINS: Record<VideoAlign, string> = {
  left: '0 auto 0 0',
  center: '0 auto',
  right: '0 0 0 auto',
};
const FIXED_ASPECT_RATIOS: Record<Exclude<VideoAspectRatio, 'original'>, string> = {
  wide: '16 / 9',
  square: '1 / 1',
  portrait: '9 / 16',
};

const VideoFigure = styled.figure`
  margin: var(--cms-video-margin, 0 auto);
  max-width: 100%;
  width: var(--cms-video-width, 100%);

  @media ${mediaQueries.largePhone} {
    width: var(--cms-video-mobile-width, var(--cms-video-width, 100%));
  }
`;

const VideoFrame = styled.div`
  aspect-ratio: var(--cms-video-aspect-ratio, 16 / 9);
  background: var(--dcm-cms-video-frame-bg, #171a1c);
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const fillFrame = css`
  border: 0;
  display: block;
  height: 100%;
  inset: 0;
  position: absolute;
  width: 100%;
`;

const NativeVideo = styled.video`
  ${fillFrame}
  background: var(--dcm-cms-video-frame-bg, #171a1c);
  object-fit: contain;
`;

const ProviderFrame = styled.iframe`
  ${fillFrame}
  background: var(--dcm-cms-video-frame-bg, #171a1c);
`;

const PosterImage = styled.img`
  ${fillFrame}
  object-fit: contain;
`;

const fallbackSurface = css`
  ${fillFrame}
  align-items: center;
  background: var(--dcm-cms-video-fallback-bg, #171a1c);
  display: flex;
  color: var(--dcm-cms-video-fallback-color, #fff);
  justify-content: center;
  padding: 0;
`;

const PlayButton = styled.button`
  ${fallbackSurface}
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid var(--dcm-cms-video-focus-color, #fff);
    outline-offset: -5px;
  }
`;

const EditorSurface = styled.div`
  ${fallbackSurface}
  display: flex;
`;

const PlayDisclosure = styled.span`
  align-items: center;
  background: var(--dcm-cms-video-play-bg, rgb(0 0 0 / 72%));
  border-radius: var(--dcm-cms-video-play-radius, 999px);
  color: var(--dcm-cms-video-play-color, #fff);
  display: inline-flex;
  font-family: var(--dcm-cms-video-play-font-family, ${FONT_ROLES.body});
  font-size: var(--dcm-cms-video-play-font-size, 14px);
  font-weight: 600;
  gap: 8px;
  min-height: 44px;
  padding: 10px 16px;
  position: relative;
`;

const ErrorPanel = styled.div`
  align-items: center;
  background: var(--dcm-cms-video-error-bg, rgb(0 0 0 / 82%));
  color: var(--dcm-cms-video-error-color, #fff);
  display: flex;
  flex-direction: column;
  font-family: var(--dcm-cms-video-error-font-family, ${FONT_ROLES.body});
  gap: 8px;
  inset: auto 0 0;
  padding: 12px;
  position: absolute;
  text-align: center;
  z-index: 2;
`;

const RetryButton = styled.button`
  background: var(--dcm-cms-video-error-action-bg, #fff);
  border: 0;
  border-radius: var(--dcm-cms-video-error-action-radius, 4px);
  color: var(--dcm-cms-video-error-action-color, #171a1c);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 8px 12px;

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-video-focus-color, #fff);
    outline-offset: 2px;
  }
`;

const ProviderFallbackLink = styled.a`
  color: var(--dcm-cms-video-provider-link-color, #356fdb);
  display: inline-block;
  font-family: var(--dcm-cms-video-provider-link-font-family, ${FONT_ROLES.body});
  font-size: var(--dcm-cms-video-provider-link-font-size, ${BODY_TEXT_SIZES.sm});
  font-weight: 600;
  margin: var(--dcm-cms-video-provider-link-margin, 8px 0 0);
  text-decoration: underline;

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-video-provider-link-focus-color, #356fdb);
    outline-offset: 2px;
  }
`;

const VideoCaption = styled.figcaption`
  color: var(--dcm-cms-video-caption-color, ${TEXT_COLOR_ROLES.muted});
  font-family: var(--dcm-cms-video-caption-font-family, ${FONT_ROLES.body});
  font-size: var(--dcm-cms-video-caption-font-size, ${BODY_TEXT_SIZES.sm});
  line-height: var(--dcm-cms-video-caption-line-height, ${BODY_LINE_HEIGHT});
  margin: var(--dcm-cms-video-caption-margin, 8px 0 0);
`;

const IncompletePlaceholder = styled.div`
  align-items: center;
  aspect-ratio: var(--cms-video-aspect-ratio, 16 / 9);
  background: var(--dcm-cms-video-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-video-placeholder-color, #646d72);
  display: flex;
  font-family: var(--dcm-cms-video-placeholder-font-family, ${FONT_ROLES.body});
  font-size: 14px;
  justify-content: center;
  margin: var(--cms-video-margin, 0 auto);
  max-width: 100%;
  padding: 16px;
  text-align: center;
  width: var(--cms-video-width, 100%);

  @media ${mediaQueries.largePhone} {
    width: var(--cms-video-mobile-width, var(--cms-video-width, 100%));
  }
`;

type NormalizedVideoLayout = {
  width: VideoWidth;
  customWidth: number;
  align: VideoAlign;
  mobile: VideoMobileWidth;
  aspectRatio: VideoAspectRatio;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && allowed.includes(value as T) ? (value as T) : fallback;
}

export function normalizeVideoLayout(value: unknown): NormalizedVideoLayout {
  const layout = isRecord(value) ? value : {};
  const customWidth =
    typeof layout.customWidth === 'number' && Number.isInteger(layout.customWidth)
      ? Math.min(MAX_CUSTOM_WIDTH, Math.max(1, layout.customWidth))
      : DEFAULT_CUSTOM_WIDTH;
  return {
    width: normalizeEnum(
      layout.width,
      WIDTH_OPTIONS.map(({ value: option }) => option),
      DEFAULT_LAYOUT.width
    ),
    customWidth,
    align: normalizeEnum(
      layout.align,
      ALIGN_OPTIONS.map(({ value: option }) => option),
      DEFAULT_LAYOUT.align
    ),
    mobile: normalizeEnum(
      layout.mobile,
      MOBILE_OPTIONS.map(({ value: option }) => option),
      DEFAULT_LAYOUT.mobile
    ),
    aspectRatio: normalizeEnum(
      layout.aspectRatio,
      ASPECT_RATIO_OPTIONS.map(({ value: option }) => option),
      DEFAULT_LAYOUT.aspectRatio
    ),
  };
}

function resolvedWidth(layout: NormalizedVideoLayout, source: NormalizedVideoSource | null): string {
  if (layout.width === 'custom') return `${layout.customWidth}px`;
  if (layout.width === 'fit') {
    const intrinsicWidth = source?.kind === 'asset' && source.width ? source.width : DEFAULT_CUSTOM_WIDTH;
    return `${Math.min(MAX_CUSTOM_WIDTH, intrinsicWidth)}px`;
  }
  return VIDEO_WIDTHS[layout.width];
}

function resolvedAspectRatio(layout: NormalizedVideoLayout, source: NormalizedVideoSource | null): string {
  if (layout.aspectRatio !== 'original') return FIXED_ASPECT_RATIOS[layout.aspectRatio];
  if (source?.kind === 'asset' && source.width && source.height) return `${source.width} / ${source.height}`;
  return FIXED_ASPECT_RATIOS.wide;
}

function Fallback({
  accessibleName,
  posterSrc,
  provider,
  onActivate,
  editing = false,
}: {
  accessibleName: string;
  posterSrc: string;
  provider?: 'Vimeo' | 'YouTube';
  onActivate?: () => void;
  editing?: boolean;
}): JSX.Element {
  const disclosure = provider ? `Play on ${provider}` : 'Play video';
  const content = (
    <>
      {posterSrc && <PosterImage src={posterSrc} alt='' />}
      <PlayDisclosure aria-hidden='true'>▶ {disclosure}</PlayDisclosure>
    </>
  );

  if (editing || !onActivate) return <EditorSurface data-testid='cms-video-fallback'>{content}</EditorSurface>;
  const label = provider ? `Play on ${provider}: ${accessibleName}` : `Play video: ${accessibleName}`;
  return (
    <PlayButton type='button' onClick={onActivate} aria-label={label}>
      {content}
    </PlayButton>
  );
}

function NativePlayer({
  source,
  accessibleName,
  posterSrc,
}: {
  source: Extract<NormalizedVideoSource, { kind: 'asset' }>;
  accessibleName: string;
  posterSrc: string;
}): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const [activated, setActivated] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (activated && !failed) videoRef.current?.focus();
    if (failed) retryButtonRef.current?.focus();
  }, [activated, failed]);

  const handleActivate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (failed && (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE || video.error)) video.load();
    setFailed(false);
    setActivated(true);
    try {
      void Promise.resolve(video.play()).catch(() => {
        setActivated(false);
        setFailed(true);
      });
    } catch {
      setActivated(false);
      setFailed(true);
    }
  }, [failed]);

  return (
    <>
      <NativeVideo
        ref={videoRef}
        controls={activated}
        playsInline
        preload='none'
        poster={posterSrc || undefined}
        aria-label={accessibleName}
        aria-hidden={!activated ? true : undefined}
        title={accessibleName}
        tabIndex={activated ? 0 : -1}
        onError={() => {
          if (activated) {
            setActivated(false);
            setFailed(true);
          }
        }}
      >
        <source src={source.src} type={source.mimeType} />
      </NativeVideo>
      {!activated && !failed && (
        <Fallback accessibleName={accessibleName} posterSrc={posterSrc} onActivate={handleActivate} />
      )}
      {failed && (
        <>
          <Fallback accessibleName={accessibleName} posterSrc={posterSrc} />
          <ErrorPanel role='status' aria-live='polite'>
            <span>Video unavailable.</span>
            <RetryButton ref={retryButtonRef} type='button' onClick={handleActivate}>
              Try again
            </RetryButton>
          </ErrorPanel>
        </>
      )}
    </>
  );
}

function ProviderPlayer({
  source,
  accessibleName,
  posterSrc,
}: {
  source: Extract<NormalizedVideoSource, { kind: 'embed' }>;
  accessibleName: string;
  posterSrc: string;
}): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [activated, setActivated] = useState(false);
  const [failed, setFailed] = useState(false);
  const providerLabel = source.provider === 'youtube' ? 'YouTube' : 'Vimeo';

  useEffect(() => {
    if (activated && !failed) iframeRef.current?.focus();
  }, [activated, failed]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!activated || !iframe) return undefined;
    const handleError = (): void => setFailed(true);
    iframe.addEventListener('error', handleError);
    return () => iframe.removeEventListener('error', handleError);
  }, [activated]);

  if (!activated || failed) {
    return (
      <>
        <Fallback
          accessibleName={accessibleName}
          posterSrc={posterSrc}
          provider={providerLabel}
          onActivate={failed ? undefined : () => setActivated(true)}
        />
        {failed && (
          <ErrorPanel role='status' aria-live='polite'>
            <span>Video unavailable.</span>
          </ErrorPanel>
        )}
      </>
    );
  }

  return (
    <ProviderFrame
      ref={iframeRef}
      src={source.embedUrl}
      title={accessibleName}
      allow='autoplay; encrypted-media; fullscreen; picture-in-picture'
      allowFullScreen
      referrerPolicy='strict-origin-when-cross-origin'
      onError={() => setFailed(true)}
    />
  );
}

export const videoConfig: ComponentConfig<VideoProps> = {
  label: 'Video',
  ai: {
    instructions:
      'A meaningful, in-flow interactive video. It requires a specific accessible name and an Asset Library MP4/WebM or a single allowlisted YouTube/Vimeo URL. Hosted spoken video must already have open captions until synchronized caption tracks are supported. Never use this as decorative background motion.',
  },
  fields: {
    source: videoSourceField,
    poster: videoPosterField,
    accessibleName: {
      type: 'text',
      label: 'Accessible name',
      placeholder: 'Describe this video specifically',
      ai: {
        instructions:
          'Required specific human-readable name for the video. Do not use a generic value such as "Video", "Play video", or "Watch video".',
      },
    },
    caption: {
      type: 'text',
      label: 'Visible caption',
      placeholder: 'Optional caption shown below the video',
    },
    layout: {
      type: 'object',
      label: 'Layout',
      objectFields: {
        width: selectField('Width', WIDTH_OPTIONS),
        customWidth: {
          type: 'number',
          label: 'Width in pixels',
          min: 1,
          max: MAX_CUSTOM_WIDTH,
        },
        align: {
          type: 'radio',
          label: 'Alignment',
          options: [...ALIGN_OPTIONS],
          ai: { schema: { type: 'string', enum: ALIGN_OPTIONS.map(({ value }) => value) } },
        },
        mobile: selectField('On phones', MOBILE_OPTIONS),
        aspectRatio: selectField('Shape', ASPECT_RATIO_OPTIONS),
      },
    },
  },
  resolveFields: (data, { fields }) => {
    const layoutValue: Record<string, unknown> = isRecord(data.props.layout) ? data.props.layout : {};
    const width = layoutValue.width ?? DEFAULT_LAYOUT.width;
    const layoutField = fields.layout;
    if (layoutField.type !== 'object') return fields;
    const { width: widthField, customWidth, align, mobile, aspectRatio } = layoutField.objectFields;
    return {
      ...fields,
      layout: {
        ...layoutField,
        objectFields: {
          width: widthField,
          ...(width === 'custom' ? { customWidth } : {}),
          ...(width !== 'full' ? { align } : {}),
          mobile,
          aspectRatio,
        },
      },
    } as Fields<VideoProps>;
  },
  defaultProps: {
    source: { ...EMPTY_VIDEO_ASSET_SOURCE },
    poster: { ...NO_VIDEO_POSTER },
    accessibleName: '',
    caption: '',
    layout: { ...DEFAULT_LAYOUT },
  },
  render: (rawProps) => {
    const source = normalizeVideoSource(rawProps.source);
    const poster = normalizeVideoPoster(rawProps.poster);
    const accessibleName = normalizeVideoAccessibleName(rawProps.accessibleName);
    const caption = typeof rawProps.caption === 'string' ? rawProps.caption : '';
    const layout = normalizeVideoLayout(rawProps.layout);
    // Some direct component harnesses omit `puck`; production Puck supplies it.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const isEditing = Boolean(rawProps.puck?.isEditing);
    const width = resolvedWidth(layout, source);
    const aspectRatio = resolvedAspectRatio(layout, source);
    const style = {
      '--cms-video-width': width,
      '--cms-video-mobile-width': layout.mobile === 'full' ? '100%' : width,
      '--cms-video-margin': VIDEO_MARGINS[layout.align],
      '--cms-video-aspect-ratio': aspectRatio,
    } as React.CSSProperties;

    if (!source || !accessibleName) {
      if (!isEditing) return <></>;
      const missing = [!source ? 'a valid video source' : '', !accessibleName ? 'a specific accessible name' : '']
        .filter(Boolean)
        .join(' and ');
      return (
        <IncompletePlaceholder style={style} data-testid='cms-video-incomplete'>
          Choose {missing}. Hosted spoken video must have open captions until caption tracks are supported.
        </IncompletePlaceholder>
      );
    }

    const posterSrc = poster.kind === 'none' ? '' : poster.src;
    const resolvedCaption = interpolateFromPuck(caption, rawProps.puck);
    let media: JSX.Element;
    if (isEditing) {
      let provider: 'Vimeo' | 'YouTube' | undefined;
      if (source.kind === 'embed') provider = source.provider === 'youtube' ? 'YouTube' : 'Vimeo';
      media = <Fallback accessibleName={accessibleName} posterSrc={posterSrc} provider={provider} editing />;
    } else if (source.kind === 'asset') {
      media = <NativePlayer source={source} accessibleName={accessibleName} posterSrc={posterSrc} />;
    } else {
      media = <ProviderPlayer source={source} accessibleName={accessibleName} posterSrc={posterSrc} />;
    }

    return (
      <VideoFigure style={style} data-testid='cms-video'>
        <VideoFrame>{media}</VideoFrame>
        {!isEditing && source.kind === 'embed' && (
          <ProviderFallbackLink href={source.canonicalUrl} target='_blank' rel='noopener noreferrer'>
            Open {accessibleName} on {source.provider === 'youtube' ? 'YouTube' : 'Vimeo'} (opens in a new tab)
          </ProviderFallbackLink>
        )}
        {resolvedCaption.trim() !== '' && <VideoCaption>{resolvedCaption}</VideoCaption>}
      </VideoFigure>
    );
  },
};
